import React from 'react';
import Button from '../components/common/Button';
import '../styles/features-page.css';

const features = [
  {
    label: 'Overview',
    title: 'Real-time Dashboard',
    description:
      'Get a bird\'s-eye view of every site you manage. Unreviewed reports, active sites, guards on duty, and a live activity map — all on one screen.',
    highlights: [
      'Instant alert counts for unreviewed reports',
      'Active site and guard-on-duty indicators',
      'Interactive map with GPS check-in pins',
    ],
    image: '/app-dashboard.png',
    bgImage: '/app-dashboard.png',
    alt: 'Real-time dashboard with live map',
    iconColor: 'primary',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
        <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
        <polyline points="21,15 16,10 5,21" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
  },
  {
    label: 'Reporting',
    title: 'Incident Reporting',
    description:
      'Critical incidents rise to the top automatically. Color-coded severity levels, guard details, and one-tap access to full reports keep you informed without the noise.',
    highlights: [
      'Extreme, high, and normal severity classification',
      'Guard name, timestamp, and site at a glance',
      'Expandable full report with photo attachments',
    ],
    image: '/feature-incident.png',
    bgImage: '/feature-incident.png',
    alt: 'Critical incident reports',
    iconColor: 'alt',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.29 3.86L1.82 18A2 2 0 0 0 3.54 21H20.46A2 2 0 0 0 22.18 18L13.71 3.86A2 2 0 0 0 10.29 3.86Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    label: 'Intelligence',
    title: 'Analytics & Insights',
    description:
      'Turn raw reports into actionable intelligence. Daily breakdowns, incident rates, and AI-detected patterns help you spot trends before they become problems.',
    highlights: [
      'Daily report volume with normal vs. incident breakdown',
      'AI-powered pattern detection across all sites',
      'Exportable summaries for client presentations',
    ],
    image: '/app-monitoring.png',
    bgImage: '/app-monitoring.png',
    alt: 'Analytics dashboard with charts',
    iconColor: 'primary',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    label: 'Team',
    title: 'User Management',
    description:
      'Track guard certifications, manage roles, and ensure compliance across your entire workforce. Know exactly who is qualified and when credentials expire.',
    highlights: [
      'Certification tracking with expiration alerts',
      'Role-based access: admin, guard, and client',
      'Quick view of missing vs. valid credentials',
    ],
    image: '/feature-users.png',
    bgImage: '/feature-users.png',
    alt: 'Guard certifications management',
    iconColor: 'alt',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 21V19A4 4 0 0 0 13 15H5A4 4 0 0 0 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
        <path d="M23 21V19A4 4 0 0 0 16 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M16 3.13A4 4 0 0 1 16 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    label: 'On the Go',
    title: 'Mobile-First Design',
    description:
      'Built for guards in the field. Browse sites, view report counts, and drill into details — all from a phone screen designed for dark environments and quick glances.',
    highlights: [
      'Native iOS and Android experience',
      'Per-site guard and report counts at a glance',
      'Search and filter across all sites and guards',
    ],
    image: '/feature-mobile.png',
    bgImage: '/feature-mobile.png',
    alt: 'Mobile reports interface',
    iconColor: 'primary',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    label: 'AI-Powered',
    title: 'Smart Revisions & Automation',
    description:
      'AI-powered Smart Revisions clean up guard notes into professional prose. Reports are automatically organized, reviewed, and ready to share with clients.',
    highlights: [
      'One-tap AI rewrite of guard notes',
      'Side-by-side original vs. revised comparison',
      'Automated review, share, and close workflows',
    ],
    image: '/app-generate-report.png',
    bgImage: '/feature-ai.png',
    alt: 'Smart Revision report details',
    iconColor: 'alt',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

const FeaturesPage: React.FC = () => {
  return (
    <div className="features-page">
      {/* Hero */}
      <section className="features-hero">
        <div className="features-hero__bg" style={{ backgroundImage: "url('/app-dashboard.png')" }} />
        <div className="container">
          <div className="features-hero__content fade-in-up">
            <h1 className="features-hero__headline">
              Everything Your Security&nbsp;Team Needs
            </h1>
            <p className="features-hero__sub">
              From live dashboards to AI-powered report writing, Inferium Reports
              gives you the tools to run a professional operation.
            </p>
            <div className="features-hero__buttons fade-in-up-delay">
              <Button variant="primary" size="lg" href="/contact">
                Request Demo
              </Button>
              <Button variant="outline" size="lg" href="/contact">
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase Sections */}
      {features.map((f, i) => (
        <section key={i} className="feature-showcase">
          <div
            className="feature-showcase__bg"
            style={{ backgroundImage: `url('${f.bgImage}')` }}
          />
          <div className="feature-showcase__glow" />
          <div className="container">
            <div className="feature-showcase__inner fade-in-up">
              <div className="feature-showcase__phone-wrap">
                <img
                  className="feature-showcase__phone"
                  src={f.image}
                  alt={f.alt}
                  loading="lazy"
                />
              </div>
              <div className="feature-showcase__text">
                <div className={`feature-showcase__icon${f.iconColor === 'alt' ? ' feature-showcase__icon--alt' : ''}`}>
                  {f.icon}
                </div>
                <span className={`feature-showcase__label${f.iconColor === 'alt' ? ' feature-showcase__label--alt' : ''}`}>
                  {f.label}
                </span>
                <h2 className="feature-showcase__title">{f.title}</h2>
                <p className="feature-showcase__desc">{f.description}</p>
                <ul className="feature-showcase__highlights">
                  {f.highlights.map((h, j) => (
                    <li key={j}>{h}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="features-cta">
        <div className="container">
          <div className="features-cta__inner fade-in-up">
            <h2 className="features-cta__heading">
              Ready to See It in Action?
            </h2>
            <p className="features-cta__sub">
              Schedule a walkthrough with our team and see how Inferium Reports
              can transform your daily operations.
            </p>
            <div className="features-cta__buttons">
              <Button variant="primary" size="lg" href="/contact">
                Schedule Consultation
              </Button>
              <Button variant="secondary" size="lg" href="/">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
